import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Copy,
  FileText,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { getImageUrl } from '../utils/getImageUrl';

function BookList() {
  const PAGE_LANGUAGE_OPTIONS = [
    'es_de',
    'es_en',
    'es_ru',
    'es_it',
    'es_tr',
    'es_fr',
    'es_pl',
    'de_nl',
    'de_ru',
    'de_en',
    'de_it',
    'de_fr',
    'de_tr',
    'de_pl',
    'en_nl',
    'en_pl',
    'en_it',
    'en_fr',
    'en_tr',
    'en_ru',
  ];

  const [books, setBooks] = useState([]);
  const [designPresets, setDesignPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedPdfBookId, setExpandedPdfBookId] = useState(null);
  const [expandedLanguageBookId, setExpandedLanguageBookId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBooks();
    fetchDesignPresets();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get('/api/list_books');
      setBooks(response.data);
    } catch (err) {
      setError('Failed to fetch books');
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignPresets = async () => {
    try {
      const response = await axios.get('/api/design-presets', {
        withCredentials: true,
      });
      setDesignPresets(response.data || []);
    } catch (err) {
      setDesignPresets([]);
    }
  };

  const handleDeleteBook = async (bookId, bookName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${bookName}"? This will also delete all pages associated with this book. This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await axios.delete(`/api/delete_book/${bookId}`);
      fetchBooks();
    } catch (err) {
      setError('Failed to delete book');
    }
  };

  const handleDuplicateBook = async (bookId, bookName) => {
    if (
      !window.confirm(
        `Create a duplicate of "${bookName}" with all pages? ` +
          'The duplicate will start without generated page renders ' +
          'so you can regenerate them under the new book ID.',
      )
    ) {
      return;
    }

    setError('');

    try {
      await axios.post(`/api/books/${bookId}/duplicate`, {
        reset_previews: true,
        reset_pdf_links: true,
      });
      fetchBooks();
    } catch (err) {
      setError('Failed to duplicate book');
    }
  };

  const getPageCount = (book) => book.page_count || book.pages?.length || 0;

  const getPdfReleases = (book) => {
    if (Array.isArray(book.pdf_versions)) {
      return book.pdf_versions.filter((v) => v?.url);
    }

    const releases = [];
    Object.keys(book).forEach((key) => {
      if (key.startsWith('pdf_url_') && book[key]) {
        const lang = key.replace('pdf_url_', '');
        releases.push({ language_code: lang, url: book[key] });
      }
    });
    return releases;
  };

  const formatPublicationName = (bookName, languageCode) => {
    const safeName = bookName.replace(/\s+/g, '_');
    return `${safeName}_${languageCode}.pdf`;
  };

  const getBookSizeLabel = (book) => book.size?.label || 'Not calculated';
  const getPresetLabel = (book) => {
    if (!book.default_preset_id) return 'No preset';
    const preset = designPresets.find((item) => item._id === book.default_preset_id);
    return preset?.name || 'Preset not found';
  };
  const formatLanguagePair = (pair) => pair.replace('_', '-').toUpperCase();
  const getLanguagePairs = () => {
    const prioritized = PAGE_LANGUAGE_OPTIONS.filter((pair) => pair.includes('de'));
    const remaining = PAGE_LANGUAGE_OPTIONS.filter((pair) => !pair.includes('de'));
    return [...prioritized, ...remaining];
  };

  const filteredBooks = books.filter((book) =>
    book.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading books...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-10 px-6 pb-8">
      <div className="rounded-3xl bg-white shadow-sm">
        <div className="flex items-center px-8 py-6">
          <h1 className="text-3xl font-bold">My Books</h1>
          <Button className="ml-auto bg-gray-900 hover:bg-gray-800" asChild>
            <Link to="/books/new">
              <Plus className="mr-2 h-4 w-4" />
              Start New Book
            </Link>
          </Button>
        </div>

        <div className="px-8 pb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search books by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="px-8 pb-8">
        {error && (
          <div className="mb-4 rounded bg-destructive/10 px-4 py-3 text-destructive">
            {error}
          </div>
        )}

        {!loading && books.length === 0 && (
          <Card className="border-dashed">
            <CardHeader className="flex flex-col items-center justify-center py-16">
              <BookOpen className="mb-4 h-12 w-12 text-muted-foreground" />
              <CardTitle className="mb-2 text-lg">No books yet</CardTitle>
              <CardDescription className="mb-6">
                Create your first book to get started
              </CardDescription>
              <Button asChild>
                <Link to="/books/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Book
                </Link>
              </Button>
            </CardHeader>
          </Card>
        )}

        {filteredBooks.length === 0 && searchTerm && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              No books found matching &quot;{searchTerm}&quot;
            </p>
          </div>
        )}

        {filteredBooks.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Cover
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Name
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    # Pages
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Size
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Preset
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Languages
                  </th>
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    PDFs
                  </th>
                  <th className="px-5 py-4 text-right text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredBooks.map((book) => {
                  const pdfReleases = getPdfReleases(book);
                  const pageCount = getPageCount(book);
                  const isExpanded = expandedPdfBookId === book._id;
                  const languagePairs = getLanguagePairs();
                  const visibleLanguagePairs = languagePairs.slice(0, 3);
                  const hiddenLanguagePairs = languagePairs.slice(3);
                  const areLanguagesExpanded = expandedLanguageBookId === book._id;

                  return (
                    <tr key={book._id} className="align-top hover:bg-gray-50/60">
                      <td className="px-5 py-5">
                        {book.cover_image ? (
                          <img
                            src={getImageUrl(book.cover_image)}
                            alt={book.name}
                            className="h-16 w-16 rounded-full object-cover ring-1 ring-gray-200"
                          />
                        ) : (
                          <Link
                            to={`/books/${book._id}/edit`}
                            className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400 ring-1 ring-gray-200 hover:bg-gray-200"
                            title="Edit Book"
                          >
                            <BookOpen className="h-6 w-6" />
                          </Link>
                        )}
                      </td>

                      <td className="px-5 py-5">
                        <div className="space-y-2">
                          <Link
                            to={`/pages/${book._id}?plang=es_de`}
                            className="block text-sm font-semibold text-gray-900 hover:text-primary"
                          >
                            {book.name}
                          </Link>
                          <Link
                            to={`/books/${book._id}/edit`}
                            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80"
                          >
                            <FileText className="h-4 w-4" />
                            Book Details
                          </Link>
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-gray-900">
                            {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                          </div>
                          <Link
                            to={`/pages/${book._id}?plang=es_de`}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            View page list
                          </Link>
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        <div className="max-w-xs text-xs leading-5 text-gray-700">
                          {getBookSizeLabel(book)}
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        <div className="max-w-[180px] text-xs leading-5 text-gray-700">
                          {getPresetLabel(book)}
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        <div className="max-w-[240px] space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {visibleLanguagePairs.map((pair) => (
                              <Link
                                key={pair}
                                to={`/pages/${book._id}?plang=${pair}`}
                                className="inline-flex items-center rounded-full border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                              >
                                {formatLanguagePair(pair)}
                              </Link>
                            ))}
                            {hiddenLanguagePairs.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedLanguageBookId(
                                    areLanguagesExpanded ? null : book._id,
                                  )
                                }
                                className="inline-flex items-center rounded-full border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                              >
                                {areLanguagesExpanded
                                  ? 'Show less'
                                  : `+${hiddenLanguagePairs.length} more`}
                              </button>
                            )}
                          </div>
                          {areLanguagesExpanded && hiddenLanguagePairs.length > 0 && (
                            <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                              {hiddenLanguagePairs.map((pair) => (
                                <Link
                                  key={pair}
                                  to={`/pages/${book._id}?plang=${pair}`}
                                  className="inline-flex items-center rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                                >
                                  {formatLanguagePair(pair)}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-5">
                        {pdfReleases.length === 0 ? (
                          <span className="text-sm text-gray-400">No PDFs</span>
                        ) : (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedPdfBookId(isExpanded ? null : book._id)
                              }
                              className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                            >
                              <span>{pdfReleases.length} PDF{pdfReleases.length === 1 ? '' : 's'}</span>
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                {pdfReleases.map((release, index) => (
                                  <a
                                    key={index}
                                    href={release.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm text-primary hover:text-primary/80 hover:underline"
                                  >
                                    {formatPublicationName(book.name, release.language_code)}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-5 py-5">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleDuplicateBook(book._id, book.name)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            title="Duplicate Book"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <Link
                            to={`/books/${book._id}/edit`}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            title="Edit Book"
                          >
                            <Pencil className="h-4 w-4" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDeleteBook(book._id, book.name)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                            title="Delete Book"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default BookList;
